import scaleRange from './scaleRange';
import matrixColumnLabel from './matrixColumnLabel';
import isStandardQuestion from './isStandardQuestion';
import shouldShowMatrixRightColumn from './shouldShowMatrixRightColumn';
import splitMatrixColumns from './matrixScaleColumns';
import scaleStartLabel from './scaleStartLabel';

// De schuifbalk (Visual Analog Scale): van een plek op de balk naar het antwoord
// dat een rondje zou hebben opgeleverd, en de teksten aan de uiteinden.
//
// Nodig omdat `scaleRange` alleen de reeks levert; de omrekening en de keuze van
// de ankers bestaan nergens anders. Het antwoord wordt opgeslagen alsof er een
// rondje is aangeklikt, zodat geen enkele analyse de balk hoeft te kennen.
// AI-CONTEXT: See docs/features/vas-slider.md

const VAS_MAX = 100;

/**
 * @typedef {object} MatrixColumn
 * @property {string} ID
 * @property {number|string} [Position]
 * @property {string|number} [Title]
 */

/**
 * @typedef {object} VasAnchors
 * @property {string} left - Tekst bij de laagste waarde
 * @property {string} right - Tekst bij de hoogste waarde
 */

/**
 * Welk vak hoort bij een plek: `k` gelijke vakken, de grens hoort bij het
 * hogere vak, 100 bij het laatste. Niet `round(p · (k − 1))` — dan krijgen de
 * uitersten maar half zo veel ruimte als het midden.
 *
 * @param {number} position - Plek op de balk, 0 (links) t/m 100 (rechts)
 * @param {number} count - Aantal vakken
 * @returns {number|null} Nulgebaseerd vak, of null bij onbruikbare invoer
 */
export const positionToIndex = (position, count) => {
    if (!Number.isInteger(count) || count < 1) return null;
    if (!Number.isFinite(position) || position < 0 || position > VAS_MAX) return null;

    return Math.min(count - 1, Math.floor((position / VAS_MAX) * count));
};

/**
 * @param {object} field - Een `scale`-vraag met `ReachStart` en `ReachEnd`
 * @param {number} position - Plek op de balk, 0 t/m 100
 * @returns {number|null} Het antwoord binnen het bereik van de vraag
 */
export const positionToScaleValue = (field, position) => {
    const range = scaleRange(field);
    const index = positionToIndex(position, range.length);

    return index === null ? null : range[index];
};

/**
 * Schaalkolommen en 'niet van toepassing'-kolommen van een matrixvraag. De regel
 * zelf staat in matrixScaleColumns.js, gedeeld met de verdeling per antwoord.
 *
 * @param {object} field - Een `matrix-one`-vraag
 * @param {MatrixColumn[]} [columns] - Zijn `MatrixQuestionColumns`
 * @returns {{scale: MatrixColumn[], notApplicable: MatrixColumn[]}}
 */
export const matrixScaleColumns = (field, columns) => splitMatrixColumns(columns, field?.ReachEnd);

/**
 * @param {MatrixColumn[]} scaleColumns - Uitkomst `scale` van `matrixScaleColumns`
 * @param {number} position - Plek op de balk, 0 t/m 100
 * @returns {MatrixColumn|null}
 */
export const positionToMatrixColumn = (scaleColumns, position) => {
    const index = positionToIndex(position, scaleColumns?.length || 0);

    return index === null ? null : scaleColumns[index];
};

/**
 * @param {*} value
 * @returns {string} Getrimde tekst, `''` als er niets staat
 */
const text = (value) => String(value ?? '').trim();

/**
 * De teksten aan de uiteinden. Volgorde van voorrang:
 * 1. tweezijdige matrixrij → de linker- en rechtertekst van de rij;
 * 2. gestandaardiseerde matrixvraag → 'Helemaal niet' / 'Helemaal wel' (besluit 2026-10-14);
 * 3. overige matrix → titel van de eerste en laatste kolom binnen het bereik;
 * 4. scale → de eigen eindteksten, anders de getallen van het bereik.
 *
 * De begintekst staat in twee spellingen in de database (`ReachStartLable` en
 * bij 44 vragen `ReachStartLabel`); beide tellen.
 *
 * @param {object} params
 * @param {object} params.field - De vraag
 * @param {object} [params.row] - De matrixrij, bij `matrix-one`
 * @param {MatrixColumn[]} [params.columns] - De kolommen, bij `matrix-one`
 * @param {Object<string, string>} [params.translations] - Vertaalde teksten, op brontekst gesleuteld
 * @returns {VasAnchors}
 */
export const vasAnchors = ({ field, row, columns, translations }) => {
    const translate = (value) => translations?.[value] || value;

    if (field?.Type === 'matrix-one') {
        if (row && shouldShowMatrixRightColumn(field)) {
            return { left: translate(text(row.Title)), right: translate(text(row.TitleRight)) };
        }
        if (isStandardQuestion(field)) {
            return { left: translate('Helemaal niet'), right: translate('Helemaal wel') };
        }
        const { scale } = matrixScaleColumns(field, columns);
        if (scale.length === 0) return { left: '', right: '' };
        const last = scale.length - 1;
        return {
            left: matrixColumnLabel(scale[0], 0, translations),
            right: matrixColumnLabel(scale[last], last, translations),
        };
    }

    const range = scaleRange(field);
    const start = text(scaleStartLabel(field));
    const end = text(field?.ReachEndLabel);

    return {
        left: start ? translate(start) : String(range[0] ?? ''),
        right: end ? translate(end) : String(range[range.length - 1] ?? ''),
    };
};

export default vasAnchors;
